/**
 * useAdminEventDetails Hook
 *
 * Fetches event details for admin orders context.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AdminEventDetailsDocument = `
  query AdminEventDetails($id: ID!) {
    adminEvent(id: $id) {
      id
      title
      startsAt
      imageUrl
      venue
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface AdminEventDetails {
  id: string;
  title: string;
  startsAt: string;
  imageUrl: string | null;
  venue: string | null;
}

interface UseAdminEventDetailsOptions {
  eventId: string;
  pause?: boolean;
}

interface UseAdminEventDetailsResult {
  event: AdminEventDetails | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch event details for admin orders page.
 */
export function useAdminEventDetails(
  options: UseAdminEventDetailsOptions
): UseAdminEventDetailsResult {
  const { eventId, pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    adminEvent: AdminEventDetails | null;
  }>({
    query: AdminEventDetailsDocument,
    variables: { id: eventId },
    pause: pause || !eventId,
  });

  return {
    event: result.data?.adminEvent ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
